import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartContext = createContext();

export function CartProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const isAuthed = !!user;
  const [cart, setCart] = useState({ items: [], total: 0, currency: 'USD' });
  const [loading, setLoading] = useState(false);
  const [civitatisCount, setCivitatisCount] = useState(0);
  const pollRef = useRef(null);

  const fetchCart = useCallback(async () => {
    // Anonymous visitor → /api/cart returns 401. Skip the request entirely
    // so public pages (PJR Select, Grupos, product detail) load instantly
    // for unauth'd traffic instead of paying for a guaranteed 401.
    if (!isAuthed) {
      setCart({ items: [], total: 0, currency: 'USD' });
      return null;
    }
    try {
      const response = await axios.get(`${API_URL}/api/cart`, {
        withCredentials: true,
      });
      setCart(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }, [isAuthed]);

  const fetchCivitatisCount = useCallback(async () => {
    // Same gating as fetchCart — endpoint is auth-required, skip when anon.
    if (!isAuthed) { setCivitatisCount(0); return; }
    try {
      const r = await axios.get(`${API_URL}/api/civitatis/cart`, { withCredentials: true });
      setCivitatisCount(r.data?.cart?.items?.length || 0);
    } catch {
      setCivitatisCount(0);
    }
  }, [isAuthed]);

  useEffect(() => {
    // Wait for the AuthProvider boot probe to finish before deciding to
    // fetch — prevents the brief race where `isAuthed` flickers to false
    // while /api/auth/me is still in-flight, which would otherwise skip
    // the fetch and force a manual reload after login.
    if (authLoading) return;
    fetchCart();
    fetchCivitatisCount();
  }, [authLoading, fetchCart, fetchCivitatisCount]);

  // Polling: when the cart contains a Europamundo hold, refetch every 30s
  // so the countdown badge reflects the live `prereserve_minutes_left`.
  // Pure read-only — no mutations, low cost.
  useEffect(() => {
    const hasHold = (cart.items || []).some(it => it.provider_id_reserva);
    if (!hasHold) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(() => { fetchCart(); }, 30_000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [cart.items, fetchCart]);

  const addToCart = async (item) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/cart/add`,
        item,
        { withCredentials: true }
      );
      setCart(response.data);
      return { success: true };
    } catch (error) {
      console.error('Error adding to cart:', error);
      return { success: false, error: 'Failed to add item to cart' };
    } finally {
      setLoading(false);
    }
  };

  // Pre-reserved tour add: synchronous SOAP `setPreReservaEmailTlfPrivado`
  // happens in the backend; on success the cart line carries booking_id,
  // provider_id_reserva and fecha_limite_confirma. The frontend uses these
  // to render the countdown banner and ultimately fire `confirmaPreReserva`
  // through the payment endpoint.
  const addPreReservedTour = async (payload) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/cart/add-prereserved-tour`,
        payload,
        { withCredentials: true }
      );
      if (response.data?.cart) setCart(response.data.cart);
      return {
        success: true,
        booking_id: response.data?.booking_id,
        provider_id_reserva: response.data?.provider_id_reserva,
        pax_emv_ids: response.data?.pax_emv_ids || [],
      };
    } catch (error) {
      const detail = error?.response?.data?.detail;
      // Structured `{code, message, ...}` payload coming from the new
      // EM_PROVIDER_ERROR contract. Forward it as-is so the caller can
      // surface a diagnostic modal with localizador + error code.
      const isStructured = detail && typeof detail === 'object' && !Array.isArray(detail);
      let msg;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors look like
        //   [{type, loc: ['body', 'field'], msg, ...}, ...]
        // Surface the first 2-3 issues so the agent knows what's missing
        // instead of seeing a generic "Error al crear pre-reserva".
        const fields = detail.slice(0, 3).map((e) => {
          const path = Array.isArray(e?.loc) ? e.loc.slice(1).join('.') : 'campo';
          return `${path}: ${e?.msg || e?.type || 'inválido'}`;
        });
        msg = `Validación: ${fields.join(' · ')}`;
      } else {
        msg = detail?.message || detail?.primary || error.message || 'Pre-reserve failed';
      }
      return {
        success: false,
        error: msg,
        errorDetail: isStructured ? detail : null,
        status: error?.response?.status,
      };
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (cartItemId) => {
    setLoading(true);
    try {
      const response = await axios.delete(
        `${API_URL}/api/cart/remove/${cartItemId}`,
        { withCredentials: true }
      );
      setCart(response.data);
      return { success: true };
    } catch (error) {
      console.error('Error removing from cart:', error);
      return { success: false, error: 'Failed to remove item from cart' };
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/api/cart/clear`, {
        withCredentials: true,
      });
      setCart({ items: [], total: 0, currency: 'USD' });
      return { success: true };
    } catch (error) {
      console.error('Error clearing cart:', error);
      return { success: false, error: 'Failed to clear cart' };
    } finally {
      setLoading(false);
    }
  };

  const itemCount = cart.items?.length || 0;
  const hasExpiringHold = (cart.items || []).some(
    it => it.prereserve_status === 'warning' || it.prereserve_status === 'expired'
  );

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        addToCart,
        addPreReservedTour,
        removeFromCart,
        clearCart,
        fetchCart,
        itemCount,
        hasExpiringHold,
        civitatisCount,
        fetchCivitatisCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
